import { LoadingOverlay } from "@mantine/core";
import { ArrowRightIcon } from "@modulz/radix-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { useTokenDetails } from "../../context/token-details-context";
import { useGetInstitutionWallets } from "../../hooks/useWallets";
import { MEMBER_ROUTE } from "../../lib/constants";
import { formatAmountWithDecimals, formatEntity } from "../../lib/utils";
import { NameValue } from "../NameValue";
import { ComparisonChart } from "../charts/ComparisonChart";
import { CustomPieChart } from "../charts/CustomPieChart";
import { ManualDistributionForm } from "../modals/ManualDistributionForm";
import { ParagraphBold, Title } from "../styled";
import CopyToClipboard from "../copyToClipboard";

const StyledNameValue = styled(NameValue)`
  margin: 8px 0;
  font-size: 0.75em;
  line-height: 1.5em;

  ${NameValue.Name} {
    color: var(--textColor);
  }

  ${NameValue.Value} {
    color: var(--textColor);
    font-weight: 600;
  }
`;

export const CirculationComponent = () => {
	const {
		tokenDetails: _walletBalanceAndTokenDetails,
		isLoadingWalletTokenDetails,
		walletSummaryDetails,
	} = useTokenDetails();

	const navigate = useNavigate();
	const [distributeFormModalOpened, setDistributeFormModalOpened] =
		useState<boolean>(false);

	const wallets = walletSummaryDetails?.wallets || [];
	const institutionWallet = wallets?.find(
		(wallet) => wallet?.category === "Institution",
	);
	const masterReserveWallet = wallets?.find(
		(wallet) => wallet?.category === "Master",
	);
	const distributionWallet = wallets?.find(
		(wallet) => wallet?.category === "Distribution",
	);

	const theme = useTheme();
	const {
		green,
		yellow,
		blue: primaryBlue,
	} = theme.colors.primary;
	const colors = [green, primaryBlue, yellow];

	const { t } = useTranslation();
	const {
		data: institutionWallets = [],
		isLoading: isLoadingInstitutionWallets,
	} = useGetInstitutionWallets();

	const tokensData = [
		{
			name: t("circulating.supply.description"),
			value: walletSummaryDetails?.inCirculation || 0,
		},
		{
			name: t("non.circulating.supply.description"),
			value: walletSummaryDetails?.notInCirculation || 0,
		},
	];

	const institutionBalance = institutionWallet?.balances?.[0]?.amount;
	const masterBalance = masterReserveWallet?.balances?.[0]?.amount;
	const distributionBalance = distributionWallet?.balances?.[0]?.amount;

	const internalWalletOptions = [
		{
			label: t("master.title"),
			color: "var(--textColor)",
			value: Number(masterBalance),
		},
		{
			label: t("distribution.title"),
			color: green,
			value: Number(distributionBalance),
		},
	];

	const internalWalletTotal = internalWalletOptions
		.map((option) => option.value)
		.reduce((a, b) => a + b);
	const masterWalletPercentage =
		(Number(masterBalance) / internalWalletTotal) * 100;
	const distributionWalletPercentage =
		(Number(distributionBalance) / internalWalletTotal) * 100;
	return (
		<div>
			<Card style={{ height: 300 }}>
				<LoadingOverlay
					visible={isLoadingWalletTokenDetails}
					overlayColor="var(--accentBg)"
				/>
				<TokensHeaderWrapper style={{ marginBottom: 0 }}>
					<Title>{t("total.supply.description")}</Title>
					<Title>
						<TokensAmount>
							{walletSummaryDetails?.supply
								? formatAmountWithDecimals(
										walletSummaryDetails?.supply,
										walletSummaryDetails?.decimals,
									)
								: "0"}
						</TokensAmount>
						{walletSummaryDetails?.symbol}
					</Title>
				</TokensHeaderWrapper>
				<TokensChartWrapper>
					<CustomPieChart
						data={tokensData}
						tokenSymbol={walletSummaryDetails?.symbol}
					/>
				</TokensChartWrapper>
				<LinkContainer>
					<DashboardLink>
						<LinkText
							onClick={() => {
								navigate(MEMBER_ROUTE.CURRENCY_MANAGEMENT);
							}}
						>
							{t("tokens.manage")}
						</LinkText>
						<ArrowRightIcon fontWeight={600} color={green} />
					</DashboardLink>
				</LinkContainer>
			</Card>

			<Card>
				<LoadingOverlay
					visible={isLoadingWalletTokenDetails || isLoadingInstitutionWallets}
					overlayColor="var(--accentBg)"
				/>
				<TokensHeaderWrapper>
					<Title>{t("wallets.institution")}</Title>
					<Title>
						<TokensAmount>
							{formatAmountWithDecimals(
								institutionBalance,
								walletSummaryDetails?.decimals,
							) || 0}
						</TokensAmount>
						{walletSummaryDetails?.symbol}
					</Title>
				</TokensHeaderWrapper>
				{institutionWallets.slice(0, 3).map((wallet, index) => {
					const walletBalance = wallet?.balances?.[0]?.amount || 0;
					const percentage =
						(Number(walletBalance) / Number(institutionBalance)) * 100;
					return (
						<WalletWrapper key={index}>
							<WalletLeftSection>
								<WalletTitle>
									<span className="flex gap-2">
										<CopyToClipboard text={formatEntity(wallet?.owner)} />
										<span className="truncate max-w-[200px]">
											{formatEntity(wallet?.owner)}
										</span>
									</span>
								</WalletTitle>
								<div style={{ width: "100%", height: 12, paddingRight: 20 }}>
									<WalletProgressBar
										style={{
											width: `${percentage}%`,
											backgroundColor: colors[index],
										}}
									/>
								</div>
							</WalletLeftSection>
							<WalletRightSection>
								<WalletAmount>
									{formatAmountWithDecimals(
										walletBalance,
										walletSummaryDetails?.decimals,
									)}
									{walletSummaryDetails?.symbol}
								</WalletAmount>
							</WalletRightSection>
						</WalletWrapper>
					);
				})}

				<LinkContainer>
					<DashboardLink>
						<LinkText
							onClick={() => {
								setDistributeFormModalOpened(true);
							}}
						>
							{t("distribute.title")}
						</LinkText>
						<ArrowRightIcon fontWeight={600} color={green} />
					</DashboardLink>
				</LinkContainer>
			</Card>

			<Card>
				<LoadingOverlay visible={isLoadingWalletTokenDetails} overlayColor="var(--accentBg)"
 />
				<TokensHeaderWrapper>
					<Title>{t("wallets.internal")}</Title>
					<Title>
						<TokensAmount>
							{formatAmountWithDecimals(
								internalWalletTotal,
								walletSummaryDetails?.decimals,
							) || 0}
						</TokensAmount>
						{walletSummaryDetails?.symbol}
					</Title>
				</TokensHeaderWrapper>

				<ComparisonChart
					options={internalWalletOptions}
					tokenSymbol={walletSummaryDetails?.symbol}
				/>
				<Section>
					<StyledNameValue
						name={`${t("wallets.master")}`}
						value={`${masterWalletPercentage ? masterWalletPercentage.toFixed(2) : "0"}%`}
					/>
					<StyledNameValue
						name={`${t("wallets.distribution")}`}
						value={`${distributionWalletPercentage ? distributionWalletPercentage.toFixed(2) : "0"}%`}
					/>
				</Section>
				<LinkContainer>
					<DashboardLink>
						<LinkText
							onClick={() => {
								navigate(MEMBER_ROUTE.WALLETS);
							}}
						>
							{t("wallets.title")}
						</LinkText>
						<ArrowRightIcon fontWeight={600} color={green} />
					</DashboardLink>
				</LinkContainer>
			</Card>

			<ManualDistributionForm
				isVisible={distributeFormModalOpened}
				setIsVisible={setDistributeFormModalOpened}
			/>
		</div>
	);
};

const WalletProgressBar = styled.div`
  width: 50%;
  background-color: ${({ theme }) => theme.colors.primary.green};
  height: 100%;
  border-radius: 4px;
`;
const WalletTitle = styled(ParagraphBold)`
  color: var(--textColor);
  font-size: 12px;
  font-family: ProximaNovaExtraBold;
  margin-bottom: 8px;
`;

const WalletAmount = styled(ParagraphBold)`
  color: var(--textColor);
  font-size: 14px;
  font-family: ProximaNovaBold;
  margin-bottom: 8px;
  & span {
    font-size: 12px;
    font-family: ProximaNova;
  }
`;

const WalletLeftSection = styled.div`
  flex: 1;
`;

const WalletRightSection = styled.div``;

const WalletWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const LinkContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--accentBg);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  position: relative;
`;

const TokensChartWrapper = styled.div`
  flex: 1;
`;

const TokensAmount = styled.span`
  font-size: 18px;
  display: inline-block;
  margin-right: 5px;
`;

const TokensHeaderWrapper = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LinkText = styled(ParagraphBold)`
  color: ${({ theme }) => theme.colors.primary.green};
  font-size: 14px;
  font-family: ProximaNova;
  margin-right: 10px;
`;

const Section = styled.div`
  margin-bottom: 10px;
`;

const DashboardLink = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;
